﻿<script setup lang="ts">
    import { inject, onMounted, watch, computed } from 'vue';
    import List from '@shared/components/List/List.vue';
    import SubscriptionListItem from './SubscriptionListItem.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import InputRadio from '@shared/components/InputRadio.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { debounce } from '@shared/utils';
    import { SubscriptionListAccess, type RenewalFilter } from './SubscriptionList.access';
    import { subscriptionStatusOptions } from './SubscriptionStatus';
    import { useRoute, useRouter } from 'vue-router';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionListAccess: SubscriptionListAccess = injectorAccess.injector.get(SubscriptionListAccess);
    const route = useRoute();
    const router = useRouter();

    const renewalFilterOptions: { display: string; key: RenewalFilter }[] = [
        { display: 'Alla', key: 'all' },
        { display: 'Uppsagda', key: 'cancelled' },
        { display: 'Uppsagda med kommande avslut', key: 'cancelled-upcoming' },
        { display: 'Utan automatisk förlängning', key: 'no-auto-renewal' },
    ];

    const statusFilterOptions: { display: string; value: number | null }[] = [
        { display: 'Alla', value: null },
        ...subscriptionStatusOptions.map((s) => ({ display: s.label, value: s.value })),
    ];

    const currentStatusFilterDisplay = computed(
        () => statusFilterOptions.find((o) => o.value === subscriptionListAccess.state.statusFilter)?.display ?? 'Alla'
    );

    const statusFilterChanged = (value: number | null) => {
        subscriptionListAccess.state.statusFilter = value;
    };

    onMounted(async () => {
        const searchParam = route.query.s as string;
        if (searchParam) {
            subscriptionListAccess.state.freeTextFilter = searchParam;
        }

        if (subscriptionListAccess.state.serviceSubscriptions == null) {
            await subscriptionListAccess.getList();
        }
    });

    watch(
        () => subscriptionListAccess.state.freeTextFilter,
        (newVal, oldVal) => {
            if (oldVal === undefined) return;

            debounce(
                async () => {
                    router.replace({ query: { ...route.query, s: newVal } });
                    await subscriptionListAccess.freeTextFilterChanged();
                },
                { delay: 300 }
            );
        },
        { immediate: true }
    );

    watch(
        () => [subscriptionListAccess.state.renewalFilter, subscriptionListAccess.state.endDateBefore, subscriptionListAccess.state.statusFilter],
        async () => {
            await subscriptionListAccess.renewalFilterChanged();
        }
    );

    const orderOptions = () => {
        return [
            { display: $Resources.CreatedLabel, key: 'Created' },
            { display: $Resources.NameLabel, key: 'ClientCompanyName' },
            { display: 'Service level', key: 'TierContactLimit' },
        ];
    };

    const showDateFilter = computed(() => {
        return subscriptionListAccess.state.renewalFilter !== 'all' || subscriptionListAccess.state.endDateBefore;
    });
</script>

<template>
    <List
        itemId="ServiceSubscriptionId"
        title="Subscriptions"
        :allLoaded="subscriptionListAccess.state.allItemsLoaded"
        :totalItemCount="subscriptionListAccess.state.serviceSubscriptionsCount"
        :perPage="30"
        :items="subscriptionListAccess.state.serviceSubscriptions"
        :loading="subscriptionListAccess.state.loading"
        :orderOptions="orderOptions()"
        :orderByValue="subscriptionListAccess.state.orderBy"
        :orderByDirection="subscriptionListAccess.state.orderByDirection"
        :serverSideFilter="true"
        :filter="subscriptionListAccess.state.freeTextFilter"
        :singleDateFilter="showDateFilter ? { date: subscriptionListAccess.state.endDateBefore } : null"
        :datePickerButtonText="'Avslutade före'"
        @update:filter="(v) => (subscriptionListAccess.state.freeTextFilter = v)"
        @update:order="(orderBy, orderByDirection) => subscriptionListAccess.orderChanged(orderBy, orderByDirection)"
        @onDateChange="(date) => (subscriptionListAccess.state.endDateBefore = date || null)"
        @loadMore="subscriptionListAccess.loadMoreItems">
        <template #toolbar>
            <button class="btn btn-default" @click="subscriptionListAccess.getList(true)">Refresh</button>

            <BSDropdownButton
                menuClass="dropdown-menu-right"
                :active="subscriptionListAccess.state.statusFilter !== null || subscriptionListAccess.state.renewalFilter !== 'all'">
                <template #toggle>
                    <span class="glyphicon glyphicon-filter m-xxs-r"></span>
                    {{ $Resources.FilterHeading }}
                </template>
                <template #content>
                    <li class="dropdown-menu-content p-0-b">
                        <h4 class="m-0-t">Filter status</h4>

                        <InputRadio
                            v-for="option in statusFilterOptions"
                            :key="option.value ?? 'all'"
                            name="subscriptionStatusFilter"
                            :value="option.value ?? 'all'"
                            :checked="subscriptionListAccess.state.statusFilter === option.value"
                            @update:checked="statusFilterChanged(option.value)">
                            <template #label>{{ option.display }}</template>
                        </InputRadio>
                    </li>
                    <li class="dropdown-menu-content p-0-b">
                        <h4 class="m-0-t">Filter renewal</h4>

                        <InputRadio
                            v-for="option in renewalFilterOptions"
                            :key="option.key"
                            name="subscriptionRenewalFilter"
                            :value="option.key"
                            :checked="subscriptionListAccess.state.renewalFilter === option.key"
                            @update:checked="subscriptionListAccess.state.renewalFilter = option.key">
                            <template #label>{{ option.display }}</template>
                        </InputRadio>
                    </li>
                </template>
            </BSDropdownButton>
        </template>
        <template #item="{ item, index }">
            <SubscriptionListItem :subscription="item" :index="index"></SubscriptionListItem>
        </template>
    </List>
</template>
